import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Checkbox, Empty, Input, Select, Space, Tabs, Tag, Typography } from "antd";
import {
  agentPlatformApi,
  AgentPlatformApiError,
  Brief,
  SupportAutomation,
  SupportCandidate,
  SupportCase,
  SupportCustomerMatch,
  SupportEvidence,
  SupportReviewAction,
} from "./api";
import { isSafeBriefSourceUrl } from "./Briefs";
import { formatAgentDateTime } from "./dateTime";
import SupportReproductionReview from "./SupportReproductionReview";
import { isClosedSupportCase, isFixSentClosure, isOtherEmail, needsSupportAttention } from "./supportCaseClassification";

const { Title, Text } = Typography;
const key = () => `support-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const activeStatuses = new Set(["queued", "investigating", "triaged", "draft_ready", "escalated"]);
const safeEvidenceUrl = (value: string | undefined, kind: string): string | undefined => {
  if (!value) return undefined;
  if (kind === "gmail") return isSafeBriefSourceUrl(value, "gmail") ? value : undefined;
  if (kind !== "support_article") return undefined;
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === "app.scribeshadow.com"
      && !url.username && !url.password && (!url.port || url.port === "443") ? value : undefined;
  } catch { return undefined; }
};
const errorCopy = (error: unknown) =>
  error instanceof AgentPlatformApiError && error.message === "support_required_action_unverified"
    ? "A required customer action needs verification. Review its original receipt before closing this case."
    : error instanceof AgentPlatformApiError && error.message === "support_customer_lookup_unavailable"
    ? "Customer matching is temporarily unavailable. You can still enter and confirm a customer account ID."
    : error instanceof AgentPlatformApiError && (error.status === 429 ||
    (error.status === 409 && error.message.includes("quota")))
    ? "The daily investigation allowance is currently used. Try again after the allowance resets."
    : error instanceof AgentPlatformApiError && error.status === 409
    ? "This case changed or another investigation is active. Refresh and try again."
    : error instanceof AgentPlatformApiError && error.status === 404
    ? "Support review is not available for this workspace."
    : error instanceof Error ? error.message : "Unable to load support cases.";
const investigationFailureCopy = (errorCode: string | undefined) => {
  switch (errorCode) {
    case "model_not_started":
      return "The investigation did not start. Try again to review this conversation.";
    case "support_budget_exceeded":
    case "brief_daily_quota_exceeded":
      return "The daily investigation allowance is currently used. Try again after the allowance resets.";
    case "brief_authority_revoked":
      return "This conversation is no longer authorized for investigation. Refresh before trying again.";
    default:
      return "The investigation did not complete. Try again to review this conversation.";
  }
};
const statusLabel = (value: string) => value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const localTime = (value: string | undefined) => formatAgentDateTime(value) || undefined;
const matchReasonLabel = (reason: SupportCustomerMatch["candidates"][number]["reasons"][number]) => ({
  exact_sender_email: "Exact sender email",
  exact_book_title: "Exact book title",
  support_code_owner: "Support code owner",
}[reason]);
const matchConfidenceLabel = (confidence: SupportCustomerMatch["candidates"][number]["confidence"]) =>
  confidence ? `Evidence rating: ${confidence[0].toUpperCase()}${confidence.slice(1)}` : undefined;

function CustomerMatchDetails({
  match,
  binding,
  onUseCustomer,
}: {
  match: SupportCustomerMatch | undefined;
  binding: SupportCase["customerBinding"];
  onUseCustomer: (customerId: string) => void;
}): React.ReactElement | null {
  if (!match) return null;
  const verifiedMatch = match.status === "matched" &&
    binding.status === "verified" &&
    binding.customerId === match.candidates[0]?.customerId &&
    binding.verificationMethod === "exact_sender_email";
  return <div className="agent-platform-support-customer-match">
    {match.status === "matched" ? <Space direction="vertical" size={4}>
      <Space wrap><Tag color={verifiedMatch ? "success" : "blue"}>{verifiedMatch ? "Customer binding verified" : "Customer match"}</Tag>
        {match.candidates[0] && <Text>{match.candidates[0].customerId}</Text>}
        {matchConfidenceLabel(match.candidates[0]?.confidence) && <Tag>{matchConfidenceLabel(match.candidates[0]?.confidence)}</Tag>}</Space>
      {match.candidates[0]?.reasons.length ? <Text type="secondary">Matched by {match.candidates[0].reasons.map(matchReasonLabel).join(", ")}.</Text> : null}
      {!verifiedMatch && match.candidates[0] && <Button size="small" onClick={() => onUseCustomer(match.candidates[0].customerId)}>Use customer ID</Button>}
    </Space> : match.status === "ambiguous" ? <Space direction="vertical" size={4}>
      <Text strong>Possible customer matches</Text>
      {match.conflictingEvidence && <Alert type="warning" showIcon message="Conflicting match evidence" description="Verify the customer before choosing a suggested account." />}
      <Text type="secondary">Choose a customer ID only if you can verify it, then confirm the binding below.</Text>
      {match.candidates.map((candidate) => <Space key={candidate.customerId} wrap>
        <Button size="small" onClick={() => onUseCustomer(candidate.customerId)}>Use {candidate.customerId}</Button>
        {candidate.reasons.length ? <Text type="secondary">{candidate.reasons.map(matchReasonLabel).join(", ")}</Text> : null}
        {matchConfidenceLabel(candidate.confidence) && <Tag>{matchConfidenceLabel(candidate.confidence)}</Tag>}
      </Space>)}
    </Space> : <Text type="secondary">No customer match was found. You can still enter a verified customer account ID.</Text>}
    {match.requiresOperatorConfirmation && <Text type="secondary">Owner confirmation is required before a customer-specific investigation.</Text>}
    {match.coverage?.truncated && <Text type="secondary">Match search reached its limit; additional possible matches may not be shown.</Text>}
  </div>;
}

function EvidenceReferences({ evidenceIds, evidence }: {
  evidenceIds: string[]; evidence: Map<string, SupportEvidence>;
}): React.ReactElement | null {
  if (evidenceIds.length === 0) return null;
  return <ul className="agent-platform-support-evidence">{evidenceIds.map((id) => {
    const item = evidence.get(id);
    const url = item ? safeEvidenceUrl(item.url, item.kind) : undefined;
    return <li key={id}>{item ? <Space direction="vertical" size={0}>
      {url ? <a href={url} target="_blank" rel="noreferrer">{item.title}</a> : <Text>{item.title}</Text>}
      {item.summary && <Text type="secondary">{item.summary}</Text>}
      {localTime(item.observedAt) && <Text type="secondary">Observed {localTime(item.observedAt)}</Text>}
    </Space> : <Text type="secondary">Unavailable evidence reference</Text>}</li>;
  })}</ul>;
}

function AutomationStatus({ automation }: { automation: SupportAutomation }): React.ReactElement {
  const lastSynced = localTime(automation.lastSyncedAt);
  const summary = !automation.enabled
    ? "Automatic support intake is off. Manual case review remains available."
    : automation.lastErrorCode
    ? "Automatic intake needs attention. Refresh after checking the local support connection."
    : !automation.connected
    ? "Automatic intake needs an authorized connected inbox before it can check for new cases."
    : lastSynced
    ? `Automatic intake is connected. Last checked ${lastSynced}.`
    : "Automatic intake is connected and waiting for its first check.";
  return <Card size="small" className="agent-platform-support-automation" title="Automatic support intake">
    <Space direction="vertical" size={6} className="agent-platform-full">
      <Space wrap>
        <Tag color={automation.enabled && automation.connected && !automation.lastErrorCode ? "success" : "default"}>
          {automation.enabled ? automation.lastErrorCode ? "Needs attention" : automation.connected ? "Connected" : "Needs connection" : "Off"}
        </Tag>
        {automation.lastErrorCode && <Tag color="warning">Needs attention</Tag>}
        <Text>{summary}</Text>
      </Space>
      {automation.enabled && <Text type="secondary">
        {automation.pendingIntakeCount} awaiting intake
        {automation.budgetWaitingCount > 0 ? ` · ${automation.budgetWaitingCount} waiting for the daily allowance` : ""}
      </Text>}
    </Space>
  </Card>;
}

export default function SupportCases({ workspaceId, onActivityChanged, onCaseUpdated, focusCaseId, focusToken = 0 }: {
  workspaceId: string;
  onActivityChanged?: () => void;
  onCaseUpdated?: (updated: SupportCase, previous?: SupportCase) => void;
  focusCaseId?: string;
  focusToken?: number;
}): React.ReactElement {
  const [candidates, setCandidates] = useState<SupportCandidate[]>([]);
  const [cases, setCases] = useState<SupportCase[]>([]);
  const casesRef = useRef<SupportCase[]>([]);
  const [candidateId, setCandidateId] = useState<string>();
  const [customerId, setCustomerId] = useState("");
  const [customerVerified, setCustomerVerified] = useState(false);
  const [reinvestigateCustomerId, setReinvestigateCustomerId] = useState("");
  const [reinvestigateCustomerVerified, setReinvestigateCustomerVerified] = useState(false);
  const [selectedId, setSelectedId] = useState<string>();
  const [caseView, setCaseView] = useState<"open" | "closed" | "other">("open");
  const [draftText, setDraftText] = useState("");
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<string>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [discoveryBriefId, setDiscoveryBriefId] = useState<string>();
  const [automation, setAutomation] = useState<SupportAutomation>();
  const mounted = useRef(true);
  const controller = useRef<AbortController>();
  const retryKeys = useRef(new Map<string, string>());
  const handledFocusToken = useRef<number>();
  const selectedView = useRef<"open" | "closed" | "other">();
  const mutationInFlight = useRef(false);
  const selected = cases.find((item) => item.id === selectedId);
  const openCases = cases.filter((supportCase) => !isOtherEmail(supportCase) && !isClosedSupportCase(supportCase));
  const closedCases = cases.filter(isClosedSupportCase);
  const otherEmails = cases.filter(isOtherEmail);
  const visibleCases = caseView === "open" ? openCases : caseView === "closed" ? closedCases : otherEmails;
  const evidence = new Map((selected?.evidence || []).map((item) => [item.id, item]));
  const failedInvestigation = selected?.status === "failed";
  const resolutionReviewRequired = Boolean(selected?.resolutionReviewRequired);
  const fixSentClosure = Boolean(selected && isFixSentClosure(selected));
  const draftSuppressed = selected?.disposition === "skip" ||
    selected?.disposition === "escalate_development" || failedInvestigation || resolutionReviewRequired || fixSentClosure;
  const reinvestigationActive = selected && [
    "queued", "investigating", "development_pending", "development_investigating",
  ].includes(selected.status);
  const viewForCase = (supportCase: SupportCase) =>
    isOtherEmail(supportCase) ? "other" : isClosedSupportCase(supportCase) ? "closed" : "open";
  const mergeCase = useCallback((updated: SupportCase) => {
    const previous = casesRef.current.find((item) => item.id === updated.id);
    const next = previous
      ? casesRef.current.map((item) => item.id === updated.id ? updated : item)
      : [updated, ...casesRef.current];
    casesRef.current = next;
    setCases(next);
    onCaseUpdated?.(updated, previous);
  }, [onCaseUpdated]);
  const load = useCallback(async (quiet = false) => {
    controller.current?.abort(); const next = new AbortController(); controller.current = next;
    if (!quiet) { setLoading(true); setError(undefined); }
    try {
      const [candidateResult, caseResult, automationResult] = await Promise.all([
        agentPlatformApi.supportCandidates(workspaceId, next.signal),
        agentPlatformApi.supportCases(workspaceId, next.signal),
        typeof agentPlatformApi.supportAutomation === "function"
          ? agentPlatformApi.supportAutomation(workspaceId, next.signal).catch((automationError) => {
            if (automationError instanceof AgentPlatformApiError && automationError.status === 404) return undefined;
            throw automationError;
          })
          : Promise.resolve(undefined),
      ]);
      if (!mounted.current || next.signal.aborted) return;
      setCandidates(candidateResult.items); casesRef.current = caseResult.items; setCases(caseResult.items);
      setAutomation(automationResult);
      setSelectedId((current) => caseResult.items.some((item) => item.id === current)
        ? current : caseResult.items.find((item) => viewForCase(item) === "open")?.id);
    } catch (loadError) {
      if (!next.signal.aborted && mounted.current) setError(errorCopy(loadError));
    } finally { if (!quiet && !next.signal.aborted && mounted.current) setLoading(false); }
  }, [workspaceId]);
  useEffect(() => { mounted.current = true; void load(); return () => {
    mounted.current = false; controller.current?.abort();
  }; }, [load]);
  useEffect(() => { setDraftText(selected?.draft?.text || ""); }, [selected?.id, selected?.draft?.version]);
  useEffect(() => {
    if (!selected) return;
    const nextView = viewForCase(selected);
    if (selectedView.current !== undefined && selectedView.current !== nextView) {
      setCaseView(nextView);
    }
    selectedView.current = nextView;
  }, [selected?.id, selected?.status, selected?.disposition]);
  useEffect(() => {
    if (focusCaseId && focusToken !== handledFocusToken.current && cases.some((supportCase) => supportCase.id === focusCaseId)) {
      const target = cases.find((supportCase) => supportCase.id === focusCaseId);
      if (target) setCaseView(viewForCase(target));
      setSelectedId(focusCaseId);
      handledFocusToken.current = focusToken;
    }
  }, [cases, focusCaseId, focusToken]);
  useEffect(() => {
    const activeCase = cases.some((item) => [
      "queued", "investigating", "escalated", "development_pending", "development_investigating",
    ].includes(item.status));
    if (!discoveryBriefId && !activeCase && !automation?.enabled) return;
    const pollInterval = discoveryBriefId || activeCase ? 5000 : 15000;
    const timer = window.setInterval(async () => {
      // A quiet poll must never abort an operator-triggered refresh: the latter owns
      // the loading state and may be waiting on the same endpoints.
      if (!mounted.current || mutationInFlight.current || pending || loading) return;
      try {
        if (discoveryBriefId) {
          const result = await agentPlatformApi.brief(workspaceId, discoveryBriefId);
          if (["succeeded","failed"].includes(result.brief.status)) setDiscoveryBriefId(undefined);
        }
        await load(true);
      } catch { /* The visible refresh action remains the recovery path. */ }
    }, pollInterval);
    return () => window.clearInterval(timer);
  }, [automation?.enabled, cases, discoveryBriefId, load, loading, pending, workspaceId]);
  const perform = async (
    scope: string,
    operation: (idempotencyKey: string) => Promise<unknown>,
    options: { caseId?: string; onSuccess?: (result: unknown) => void } = {},
  ) => {
    if (pending || mutationInFlight.current) return;
    controller.current?.abort();
    mutationInFlight.current = true;
    const idempotencyKey = retryKeys.current.get(scope) || key();
    retryKeys.current.set(scope, idempotencyKey); setPending(scope); setError(undefined); setNotice(undefined);
    try {
      const result = await operation(idempotencyKey);
      retryKeys.current.delete(scope);
      if (mounted.current) options.onSuccess?.(result);
    }
    catch (actionError) {
      if (actionError instanceof AgentPlatformApiError && actionError.status === 409) {
        retryKeys.current.delete(scope);
        if (options.caseId) {
          try {
            const refreshed = await agentPlatformApi.supportCase(workspaceId, options.caseId);
            if (mounted.current) mergeCase(refreshed.case);
          } catch { /* Keep the conflict message when the targeted refresh also fails. */ }
        }
      }
      if (mounted.current) setError(errorCopy(actionError));
    } finally {
      mutationInFlight.current = false;
      if (mounted.current) setPending(undefined);
    }
  };
  const refreshCandidates = () => perform("refresh", async (idempotencyKey) => {
    const result = await agentPlatformApi.refreshSupportCandidates(workspaceId, idempotencyKey);
    return result;
  }, { onSuccess: (result) => {
    const discovery = result as { brief: Brief };
    if (["queued", "running"].includes(discovery.brief.status)) setDiscoveryBriefId(discovery.brief.id);
    onActivityChanged?.();
  }});
  const createCase = () => candidateId && perform(
    `create:${candidateId}:${customerVerified ? customerId.trim() : "unverified"}`, async (idempotencyKey) => {
    const result = await agentPlatformApi.createSupportCase(workspaceId, {
      candidateId, ...(customerVerified && customerId.trim() ? { customerId: customerId.trim() } : {}), idempotencyKey,
    }); return result;
  }, { onSuccess: (result) => {
    const created = (result as { case: SupportCase }).case;
    mergeCase(created); setCaseView("open"); setSelectedId(created.id); setCandidateId(undefined); setCustomerId("");
    setCustomerVerified(false);
  }});
  const review = (action: SupportReviewAction) => {
    if (!selected) return;
    const payloadScope = action === "save_draft" ? draftText.trim() : "";
    void perform(`${selected.id}:${selected.revision}:${action}:${payloadScope}`, (idempotencyKey) => agentPlatformApi.reviewSupportCase(
      workspaceId, selected, { action, idempotencyKey,
        ...(action === "save_draft" ? { draftText: draftText.trim() } : {}) }), { caseId: selected.id, onSuccess: (result) => {
      const updated = (result as { case: SupportCase }).case;
      mergeCase(updated);
      if (action === "resolve") {
        const nextOpen = openCases.find((item) => item.id !== selected.id);
        setCaseView("open"); setSelectedId(nextOpen?.id); setNotice("Case closed.");
      }
    }});
  };
  const reinvestigate = () => {
    if (!selected) return;
    const customerIdForRequest = reinvestigateCustomerVerified ? reinvestigateCustomerId.trim() : undefined;
    if (reinvestigateCustomerId.trim() && !customerIdForRequest) return;
    const target = selected;
    const scope = `reinvestigate:${target.id}:${target.revision}:${customerIdForRequest || "preserve"}`;
    void perform(scope, (idempotencyKey) => agentPlatformApi.reinvestigateSupportCase(
      workspaceId, target, { idempotencyKey, ...(customerIdForRequest ? { customerId: customerIdForRequest } : {}) },
    ), { caseId: target.id, onSuccess: (result) => mergeCase((result as { case: SupportCase }).case) });
  };
  const copyDraft = async () => {
    if (!selected?.draft?.text || draftText !== selected.draft.text) return;
    try { await navigator.clipboard.writeText(selected.draft.text); setNotice("Internal draft copied."); }
    catch { setError("The internal draft could not be copied. Select the text and copy it manually."); }
  };
  const selectView = (nextView: string) => {
    const nextCases = nextView === "open" ? openCases : nextView === "closed" ? closedCases : otherEmails;
    setCaseView(nextView as "open" | "closed" | "other");
    if (!nextCases.some((supportCase) => supportCase.id === selectedId)) setSelectedId(nextCases[0]?.id);
  };
  return <section className="agent-platform-support" aria-labelledby="support-cases-title">
    <Space className="agent-platform-briefs-heading">
      <div><Title id="support-cases-title" level={3}>Support case review</Title>
        <Text type="secondary">Review selected or automatically detected cases and internal reply drafts. Nothing is sent and no Gmail draft is created.</Text></div>
      <Button onClick={() => void load()} disabled={loading || Boolean(pending)}>Refresh cases</Button>
    </Space>
    {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(undefined)} />}
    {notice && <Alert type="success" showIcon message={notice} closable onClose={() => setNotice(undefined)} />}
    {automation && <AutomationStatus automation={automation} />}
    <details className="agent-platform-support-manual">
      <summary>Start a manual investigation from an authorized message</summary>
      <Space direction="vertical" className="agent-platform-full">
        <Text type="secondary">Loading conversations uses one daily check and no model call.</Text>
        <Space wrap>
          <Select aria-label="Support conversation" placeholder="Select a conversation" value={candidateId}
            onChange={setCandidateId} options={candidates.map((item) => ({
              value:item.id, label:`${item.senderLabel || item.accountIdentifier} — ${item.subject || "No subject"}` }))} />
          <Input aria-label="Customer account ID (optional)" placeholder="Customer account ID (optional)" value={customerId}
            onChange={(event) => setCustomerId(event.target.value)} maxLength={128} />
          <Checkbox checked={customerVerified} disabled={!customerId.trim()}
            onChange={(event) => setCustomerVerified(event.target.checked)}>
            I have verified that this conversation belongs to this customer account
          </Checkbox>
          <Button onClick={() => void refreshCandidates()} loading={pending === "refresh"}>Load conversations</Button>
          <Button type="primary" onClick={() => void createCase()} disabled={!candidateId || Boolean(pending)}>Investigate selected conversation</Button>
        </Space>
      </Space>
    </details>
    <Text type="secondary">Showing up to 50 recent support cases; this is not a total inbox count. {openCases.filter(needsSupportAttention).length} need review.</Text>
    {loading ? <Text type="secondary">Loading support cases…</Text> : cases.length === 0 ?
      <Empty description="No support cases yet." /> : <div className="agent-platform-support-layout">
      <div>
        <Tabs className="agent-platform-support-tabs" activeKey={caseView} onChange={selectView} items={[
          { key: "open", label: `Open (${openCases.length})` },
          { key: "closed", label: `Closed (${closedCases.length})` },
          { key: "other", label: `Other email (${otherEmails.length})` },
        ]} />
        {visibleCases.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={caseView === "open" ? "No open support cases." : caseView === "closed" ? "No closed support cases." : "No other email."} /> : <div className="agent-platform-support-list" role="list" aria-label={caseView === "open" ? "Open support cases" : caseView === "closed" ? "Closed support cases" : "Other email"}>
        {visibleCases.map((item) => <Button key={item.id} className="agent-platform-support-case-button"
          type={item.id === selectedId ? "primary" : "default"} onClick={() => setSelectedId(item.id)}>
          <span className="agent-platform-support-case-content">
            <span className="agent-platform-support-case-subject">{item.source.subject || item.source.senderLabel || "Support message"}</span>
            <span className="agent-platform-support-case-tags"><Tag>{isOtherEmail(item) ? "Not a support request" : isFixSentClosure(item) ? "Fix sent · closed" : statusLabel(item.status)}</Tag>
              {item.intakeKind === "automatic" && <Tag color="blue">Automatic</Tag>}</span>
          </span>
        </Button>)}
        </div>}
      </div>
      {selected && <Card id={`agent-platform-support-${selected.id}`} tabIndex={-1} size="small" title={selected.source.subject || "Support case"}>
        <Space direction="vertical" className="agent-platform-full">
          <Space wrap><Tag>{isOtherEmail(selected) ? "Not a support request" : fixSentClosure ? "Fix sent · closed" : statusLabel(selected.status)}</Tag>
            {selected.intakeKind === "automatic" && <Tag color="blue">Automatic intake</Tag>}
            <Text>{selected.source.senderLabel || selected.source.accountIdentifier}</Text>
            {localTime(selected.source.observedAt) && <Text type="secondary">{localTime(selected.source.observedAt)}</Text>}</Space>
          {!isOtherEmail(selected) && <details className="agent-platform-brief-details agent-platform-support-secondary">
            <summary>Customer and case details</summary>
            <Space direction="vertical" size={2}>
              <Text>Customer binding: {selected.customerBinding.status}</Text>
              <Text type="secondary">Account association supports internal research. It does not authenticate the correspondent or authorize disclosure.</Text>
              {selected.lifecycle && <>
                <Text>Case phase: {statusLabel(selected.lifecycle.phase)} · Next: {statusLabel(selected.lifecycle.nextResponsibleActor)}</Text>
                <Text>Diagnostics: {selected.lifecycle.diagnosticEligible ? "Current authority checks required" : "Unavailable for this case state"}</Text>
                {selected.lifecycle.closureKind && <Text>Closure: {statusLabel(selected.lifecycle.closureKind)}</Text>}
                {selected.lifecycle.blockers.map((blocker) => <Text type="warning" key={blocker}>{statusLabel(blocker)}</Text>)}
              </>}
              {selected.category && <Text>Category: {selected.category}</Text>}
            </Space>
          </details>}
          {selected.findings?.map((finding, index) => <div key={index}><Text>{finding.text}</Text>
            <EvidenceReferences evidenceIds={finding.evidenceIds} evidence={evidence} /></div>)}
          {failedInvestigation && <Alert
            type="error"
            showIcon
            message="Investigation did not complete"
            description={investigationFailureCopy(selected.errorCode)}
            action={<Button onClick={reinvestigate} disabled={Boolean(pending) || reinvestigationActive}>Retry investigation</Button>}
          />}
          {resolutionReviewRequired && <Alert
            type="info"
            showIcon
            message="Resolution needs review"
            description="This investigation suggests resolving the case. Review the evidence, then select Resolve if you confirm it."
          />}
          {fixSentClosure && <Alert
            type="success"
            showIcon
            message="Fix sent · closed"
            description="A fix was communicated; no customer confirmation is required. New replies are checked again."
          />}
          {selected.uncertainty && <details className="agent-platform-brief-details"><summary>Investigation note</summary><Alert type="warning" showIcon message="Uncertainty" description={selected.uncertainty} /></details>}
          {!!selected.capabilityGaps?.length && <Alert type="info" showIcon message="Investigation coverage gaps"
            description={selected.capabilityGaps.join("; ")} />}
          {selected.chapterRemediation && <details className="agent-platform-brief-details">
            <summary>Chapter remediation — pending approval</summary>
            <Text>Proposed credit refund and rerun costs. No refund or rerun has been authorized by this recommendation.</Text>
            <pre>{selected.chapterRemediation}</pre>
          </details>}
          {selected.recommendation && <Text>Recommendation: {selected.recommendation}</Text>}
          {selected.development && <Card size="small" className="agent-platform-support-development" title="Development handoff">
            <Space direction="vertical" size={6} className="agent-platform-full">
              <Space wrap><Tag>{statusLabel(selected.development.status)}</Tag><Text>Assigned team: Development</Text>
                {localTime(selected.development.updatedAt) && <Text type="secondary">Updated {localTime(selected.development.updatedAt)}</Text>}</Space>
              {selected.development.codeDefectStatus && <Text>Code defect status: {statusLabel(selected.development.codeDefectStatus)}.{selected.development.codeDefectStatus === "not_found" ? " This only covers the inspected scope." : ""}</Text>}
              {selected.development.reproductionStatus && <Text>DEV reproduction: {statusLabel(selected.development.reproductionStatus)}
                {selected.development.reproductionId ? ` (${selected.development.reproductionId})` : ""}</Text>}
              {selected.development.summary && <Text>{selected.development.summary}</Text>}
              {selected.development.supportFindings?.map((finding, index) => <div key={`support-${index}`}>
                <Text>Support finding: {finding.text}</Text>
                <EvidenceReferences evidenceIds={finding.evidenceIds}
                  evidence={new Map((selected.development?.evidence || []).map((item) => [item.id, item]))} />
              </div>)}
              {selected.development.observedTests?.length ? <div><Text strong>Observed tests</Text>
                {selected.development.observedTests.map((test, index) => <div key={index}>
                  <Tag>{statusLabel(test.status)}</Tag><Text>{test.summary}</Text>
                  <EvidenceReferences evidenceIds={test.evidenceIds}
                    evidence={new Map((selected.development?.evidence || []).map((item) => [item.id, item]))} />
                </div>)}
              </div> : null}
              {selected.development.refundRecommendation && <div>
                <Text strong>Refund recommendation: {statusLabel(selected.development.refundRecommendation.decision)}</Text>
                <Text> — {selected.development.refundRecommendation.reason}</Text>
                <EvidenceReferences evidenceIds={selected.development.refundRecommendation.evidenceIds}
                  evidence={new Map((selected.development?.evidence || []).map((item) => [item.id, item]))} />
              </div>}
              {selected.development.findings?.map((finding, index) => <div key={index}><Text>{finding.text}</Text>
                <EvidenceReferences evidenceIds={finding.evidenceIds}
                  evidence={new Map((selected.development?.evidence || []).map((item) => [item.id, item]))} /></div>)}
              {selected.development.affectedFiles?.map((file) => <div key={file.path}>
                <Text strong>{file.path}</Text><Text> — {file.reason}</Text>
                <EvidenceReferences evidenceIds={file.evidenceIds}
                  evidence={new Map((selected.development?.evidence || []).map((item) => [item.id, item]))} />
              </div>)}
              {selected.development.fixCaseDraft && <Alert type="info" message="Fix-case draft ready for review" description={selected.development.fixCaseDraft.limitation} />}
              {selected.development.proposedChange && <Text>Proposed change: {selected.development.proposedChange}</Text>}
              {selected.development.reproductionRequests?.length ? <div><Text strong>Proposed reproductions</Text>
                <ul>{selected.development.reproductionRequests.map((request, index) =>
                  <li key={index}><Text>{request.reason} — {request.bookId}/{request.chapterId}, {request.sourceLang} → {request.targetLangs.join(", ")}, {request.repeats} repeat(s)</Text></li>)}</ul>
              </div> : null}
              {selected.customerBinding.status === "verified" && (
                (selected.status === "triaged" && selected.development.status === "findings_ready") ||
                (selected.status === "escalated" && ["pending", "queued", "investigating", "waiting_quota", "waiting_active", "waiting_reproduction"].includes(selected.development.status))
              ) && <SupportReproductionReview
                key={`${selected.id}:${selected.revision}`} workspaceId={workspaceId} supportCase={selected}
                onApproved={() => { setNotice("Exact DEV reproduction approved."); void load(true); }} />}
              {selected.development.regressionTests?.length ? <div><Text strong>Regression tests</Text>
                <ul>{selected.development.regressionTests.map((test) => <li key={test}><Text>{test}</Text></li>)}</ul>
              </div> : null}
            </Space>
          </Card>}
          <details className="agent-platform-brief-details agent-platform-support-secondary">
            <summary>Investigate again or update customer binding</summary>
            <Space direction="vertical" size={6} className="agent-platform-full">
              <Text type="secondary">Runs a new investigation for this case. A customer account is used only when you enter it and confirm the binding.</Text>
              <CustomerMatchDetails match={selected.customerMatch} binding={selected.customerBinding}
                onUseCustomer={(customerId) => { setReinvestigateCustomerId(customerId); setReinvestigateCustomerVerified(false); }} />
              <Input aria-label="Customer account ID for reinvestigation (optional)" placeholder="Customer account ID (optional)"
                value={reinvestigateCustomerId} onChange={(event) => setReinvestigateCustomerId(event.target.value)} maxLength={128} />
              <Checkbox checked={reinvestigateCustomerVerified} disabled={!reinvestigateCustomerId.trim()}
                onChange={(event) => setReinvestigateCustomerVerified(event.target.checked)}>
                I have verified this customer account for reinvestigation
              </Checkbox>
              <Button onClick={reinvestigate} disabled={
                Boolean(pending) || reinvestigationActive || Boolean(reinvestigateCustomerId.trim() && !reinvestigateCustomerVerified)
              }>Investigate again</Button>
            </Space>
          </details>
          {!draftSuppressed && <><label><Text strong>Internal reply draft</Text><Input.TextArea aria-label="Internal reply draft" rows={7}
            value={draftText} onChange={(event) => setDraftText(event.target.value)} maxLength={5000}
            disabled={["approved_for_copy","rejected","resolved"].includes(selected.status)} /></label>
          {selected.draft?.provenance && <Text type="secondary">Draft source: {statusLabel(selected.draft.provenance)} · {statusLabel(selected.draft.approvalStatus || "unapproved")}</Text>}
          {selected.draft?.evidenceRefs?.length ? <Text type="secondary">Supporting references: {selected.draft.evidenceRefs.length}. Review them with the investigation before copying.</Text> : null}
          {selected.draft?.unresolvedAssertions?.map((unknown, index) => <Alert key={`draft-unknown-${index}`} type="warning" message="Unresolved assertion" description={unknown} />)}
          {selected.draftReplacementProposal && <details><summary>Proposed replacement reply</summary>
            <Text type="secondary">Your saved draft was preserved. Review this suggestion before editing the internal reply.</Text>
            <Input.TextArea aria-label="Proposed replacement reply" value={selected.draftReplacementProposal.text} readOnly rows={5} />
          </details>}
          <Space wrap>
            <Button type={draftText !== (selected.draft?.text || "") ? "primary" : "default"} onClick={() => review("save_draft")} disabled={!draftText.trim() || !["triaged","draft_ready","escalated"].includes(selected.status) || Boolean(pending)}>Save draft</Button>
            <Button type={selected.draft?.text && draftText === selected.draft.text ? "primary" : "default"}
              onClick={() => void copyDraft()} disabled={!selected.draft?.text || draftText !== selected.draft.text}>Copy internal draft</Button>
          </Space></>}
          {draftSuppressed && !isOtherEmail(selected) && !failedInvestigation && !resolutionReviewRequired && !fixSentClosure && <Text type="secondary">This case is being handled without an internal reply draft.</Text>}
          {!isOtherEmail(selected) && <Space wrap>
            <Button onClick={() => review("resolve")} disabled={
              (!activeStatuses.has(selected.status) && selected.status !== "approved_for_copy") || Boolean(pending) || Boolean(selected.lifecycle?.blockers.length)
            }>Resolve</Button>
            <details className="agent-platform-support-secondary"><summary>More case actions</summary><Space wrap>
              <Button onClick={() => review("triage")} disabled={selected.status !== "draft_ready" || Boolean(pending)}>Mark triaged</Button>
              <Button onClick={() => review("reject")} disabled={!["triaged","draft_ready"].includes(selected.status) || Boolean(pending)}>Reject</Button>
              <Button onClick={() => review("escalate")} disabled={!["triaged","draft_ready"].includes(selected.status) || Boolean(pending)}>Escalate</Button>
            </Space></details>
          </Space>}
        </Space>
      </Card>}
    </div>}
  </section>;
}
